import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, IconButton, Chip } from "@mui/material";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { useLazyListEBillProvidersQuery } from "store/services/eBillProviderApi";

import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTable from "components/Shared/AppTable";
import CustomTabs from "components/Shared/CustomTabs";
import HighlightedText from "components/Shared/HighlightedText";
import { useAppMeta, useTablePagination } from "hooks";

const EBillProviderListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [{ title: "eBill Providers" }];

  const tabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    {
      name: "Provider Name",
      selector: (row) => row.name,
      cell: (row) => (
        <Link
          title={row?.name}
          to={`/app/crm/ebill-provider/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.name ? (
            <HighlightedText text={row?.name} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },
    {
      name: "Country",
      selector: (row) => row.country?.name,
      cell: (row) => row?.country?.name ?? "N/A",
    },
    {
      name: "Created By",
      selector: (row) => row.createdBy?.name,
      cell: (row) => row?.createdBy?.name ?? "N/A",
    },
    {
      name: "Updated By",
      selector: (row) => row.updatedBy?.name,
      cell: (row) => row?.updatedBy?.name ?? "N/A",
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/ebill-provider/${row._id}/detail`}>
            <IconButton size="small" color="primary">
              <InfoOutlined />
            </IconButton>
          </Link>
          <Link to={`/app/crm/ebill-provider/${row._id}/edit`}>
            <IconButton size="small">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  const [
    fetchEBillProviders,
    { data: eBillProviderRes, isLoading, isFetching, error },
  ] = useLazyListEBillProvidersQuery();

  React.useEffect(() => {
    fetchEBillProviders({
      active: activeTab === "Active",
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
    });
  }, [activeTab, currentPage, fetchEBillProviders, keyword, perPage]);

  useAppMeta(`${activeTab} eBill Providers`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={tabs} activeTab={activeTab} />

        <TableFilter
          searchLabel="Search eBill Providers"
          disabled={isLoading || isFetching}
        />

        <AppTable
          columns={columns}
          data={eBillProviderRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={eBillProviderRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default EBillProviderListScreen;

const PageActions = () => (
  <Link to="/app/crm/ebill-provider/create">
    <Button startIcon={<Add />} variant="contained">
      New eBill Provider
    </Button>
  </Link>
);